import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  Container,
} from '@mui/material';
import {
  BackButton,
  OutlinedIcons,
  TravelerTagChips,
  OtherChips,
} from '@marageti/z4-lib';
import { Traveler, TravelerTags } from '@marageti/z4-sdk/lib/people';
import { TripMatch, TripMatchStatus } from '@marageti/z4-sdk/lib/travel';
import { Ticket, TicketComment, TicketSubType } from '@marageti/z4-sdk/lib/tickets';
import { Link as RouterLink } from 'react-router-dom';
import TicketCommentsPanel from './ticket-comments-panel';
import TicketEditModal from '../../components/modals/ticket-edit-modal';
import {
  EditableTicket, TicketFormData, AssigneeOption, SUBTYPE_DISPLAY_LABELS,
} from '../tickets/options';
import TicketReassignModal, { TicketReassignFormData } from '../../components/modals/ticket-reassign-modal';
import TicketDeleteModal from '../../components/modals/ticket-delete-modal';
import TravelHistoryModal, { SelectedTab } from '../../components/travel-history-modal';
import TicketStatusChip from '../../components/ticket-status-chip';
import TicketContextMenu from '../../components/ticket-context-menu';
import { TicketCloseFormData } from '../../components/modals/ticket-close-modal';

const {
  OutlinedExclamationIcon,
  OutlinedStarIcon,
  OutlinedStatusOfflineIcon,
  OutlinedOfficeBuildingIcon,
  OutlinedCashIcon,
  OutlinedInformationCircleIcon,
  OutlinedMenuAlt3Icon,
  OutlinedEmojiSadIcon,
  OutlinedBanIcon,
} = OutlinedIcons;
const { getTravelerTagChip } = TravelerTagChips;
const { ChipRepeat } = OtherChips;

const SOLD_STATUSES = new Set([
  TripMatchStatus.MatchSold,
  TripMatchStatus.MatchSoldCanceled,
  TripMatchStatus.MatchRefundedCanceled,
]);

const formatCurrency = (amount: number, currencyCode: string) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: currencyCode || 'USD',
}).format(amount);

const SUBTYPE_ICONS: Record<string, React.ReactNode> = {
  TRAVELER_ISSUE: <OutlinedExclamationIcon sx={{ width: 20, height: 20, marginLeft: 1 }} />,
  TRAVELER_BEHAVIOR: <OutlinedEmojiSadIcon sx={{ width: 20, height: 20, marginLeft: 1 }} />,
  TRAVELER_REVIEW: <OutlinedStarIcon sx={{ width: 20, height: 20, marginLeft: 1 }} />,
  TRAVELER_REQUESTED_OFFLINE: <OutlinedStatusOfflineIcon sx={{ width: 20, height: 20, marginLeft: 1 }} />,
  PARTNER_OPERATIONS: <OutlinedOfficeBuildingIcon sx={{ width: 20, height: 20, marginLeft: 1 }} />,
  PARTNER_BEHAVIOR: <OutlinedEmojiSadIcon sx={{ width: 20, height: 20, marginLeft: 1 }} />,
  PARTNER_REQUESTED_OFFLINE: <OutlinedStatusOfflineIcon sx={{ width: 20, height: 20, marginLeft: 1 }} />,
  UNREPORTED_SALE: <OutlinedCashIcon sx={{ width: 20, height: 20, marginLeft: 1 }} />,
  TRIP_CANCELLATION: <OutlinedBanIcon sx={{ width: 20, height: 20, marginLeft: 1 }} />,
  OTHER: <OutlinedInformationCircleIcon sx={{ width: 20, height: 20, marginLeft: 1 }} />,
};

const getSubtypeIcon = (subType?: TicketSubType): React.ReactNode => (
  (subType && SUBTYPE_ICONS[subType]) || <OutlinedInformationCircleIcon sx={{ width: 20, height: 20, marginLeft: 1 }} />
);

type DataRowProps = {
  label: string;
  value: string | React.ReactNode;
  action?: React.ReactNode;
};

const DataRow = ({ label, value, action }: DataRowProps) => (
  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
    <Box>
      <Typography variant="caption" color="text.primary" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="body" component="div" color="text.secondary">{value}</Typography>
    </Box>
    {action}
  </Box>
);

const TravelerTagsList = ({ traveler }: { traveler: Traveler }) => {
  const tags: React.ReactNode[] = [];

  if (traveler.tags) {
    traveler.tags.forEach((tag: string) => {
      if (Object.values(TravelerTags).includes(tag as TravelerTags)) {
        tags.push(
          <React.Fragment key={tag}>
            {getTravelerTagChip(tag as TravelerTags, 'small')}
          </React.Fragment>,
        );
      }
    });
  }

  if (traveler.totalZicassoTrips > 0) {
    tags.push(<ChipRepeat key="repeat" />);
  }

  if (tags.length === 0) return null;

  return (
    <Stack direction="row" spacing={0.5} mt={0.5}>
      {tags}
    </Stack>
  );
};

type TicketDetailViewProps = {
  ticket?: Ticket;
  traveler?: Traveler;
  isLoading: boolean;
  isEditModalOpen: boolean;
  assignees: AssigneeOption[];
  tripRequestLabel: string;
  tripMatch?: TripMatch;
  tripMatchLabel: string;
  partnerCrmProfile?: string;
  totalCommissionUSD?: number;
  onStatusChange: (status: string) => void;
  onCloseTicket: (data: TicketCloseFormData) => Promise<void>;
  isTravelHistoryOpen: boolean;
  travelHistoryTab: SelectedTab;
  onTravelHistoryTabChange: (tab: SelectedTab) => void;
  onOpenTravelHistory: () => void;
  onCloseTravelHistory: () => void;
  onOpenEdit: () => void;
  onCloseEdit: () => void;
  onSaveTicket: (data: TicketFormData) => Promise<void>;
  onReassign: () => void;
  onDelete: () => void;
  isReassignModalOpen: boolean;
  reassigningTicketAssigneeIds: string[];
  onCloseReassignModal: () => void;
  onSaveReassign: (data: TicketReassignFormData) => Promise<void>;
  isDeleteModalOpen: boolean;
  onCloseDeleteModal: () => void;
  onConfirmDelete: () => Promise<void>;
  comments: TicketComment[];
  isCommentsLoading: boolean;
  hasMoreComments: boolean;
  currentUserId?: string;
  onLoadMoreComments: () => void;
  onCreateComment: (text: string) => Promise<void>;
  onCreateFileComment: (file: File, title: string) => Promise<any>;
  onUpdateComment: (commentId: string, text: string) => Promise<void>;
  onDownloadFile: (comment: TicketComment) => void;
};

const TicketDetailView = ({
  ticket,
  traveler,
  isLoading,
  isEditModalOpen,
  assignees,
  tripRequestLabel,
  tripMatch,
  tripMatchLabel,
  partnerCrmProfile,
  totalCommissionUSD,
  onStatusChange,
  onCloseTicket,
  isTravelHistoryOpen,
  travelHistoryTab,
  onTravelHistoryTabChange,
  onOpenTravelHistory,
  onCloseTravelHistory,
  onOpenEdit,
  onCloseEdit,
  onSaveTicket,
  onReassign,
  onDelete,
  isReassignModalOpen,
  reassigningTicketAssigneeIds,
  onCloseReassignModal,
  onSaveReassign,
  isDeleteModalOpen,
  onCloseDeleteModal,
  onConfirmDelete,
  comments,
  isCommentsLoading,
  hasMoreComments,
  currentUserId,
  onLoadMoreComments,
  onCreateComment,
  onCreateFileComment,
  onUpdateComment,
  onDownloadFile,
}: TicketDetailViewProps) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <Typography variant="h5" color="text.secondary">
          Ticket not found
        </Typography>
      </Box>
    );
  }

  const isTravelerIssue = ticket.ticketSubType === 'TRAVELER_ISSUE';
  const subtypeLabel = ticket.ticketSubType
    ? (SUBTYPE_DISPLAY_LABELS[ticket.ticketSubType] || ticket.ticketSubType)
    : '';

  const editableTicket: EditableTicket = {
    id: ticket.id,
    ticketType: ticket.ticketType,
    ticketSubType: ticket.ticketSubType,
    status: ticket.status,
    title: ticket.title,
    description: ticket.description,
    travelerId: ticket.travelerId,
    travelerName: ticket.travelerName,
    travelerEmail: '',
    tripRequestId: ticket.tripRequestId,
    tripMatchId: ticket.tripMatchId,
    assignees: ticket.assignees || [],
  };

  return (
    <Container maxWidth="xl" sx={{ px: '0 !important' }}>
      <Grid container sx={{ height: 'calc(100vh - 64px)' }}>
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            borderRight: `1px solid ${theme.palette.divider}`,
            overflowY: 'auto',
            height: '100%',
          }}
        >
          <Box sx={{ pl: 6, pr: 5, py: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <BackButton defaultPath="/tickets" sx={{ p: 1, ml: -1.5 }} />
              <TicketContextMenu
                ticket={ticket}
                onEdit={onOpenEdit}
                onReassign={onReassign}
                onDelete={onDelete}
              />
            </Box>

            <Typography variant="h2" fontWeight={700} mb={2}>
              {ticket.title}
            </Typography>

            <Stack direction="row" spacing={1} mb={4}>
              <Chip
                label={subtypeLabel}
                color="default"
                icon={getSubtypeIcon(ticket.ticketSubType) as React.ReactElement}
                sx={{
                  '& .MuiChip-icon': {
                    color: isTravelerIssue ? 'error.dark' : 'text.primary',
                    marginLeft: 0.25,
                  },
                  '& .MuiChip-label': {
                    px: 0.5,
                  },
                  px: 1,
                  py: 0.5,
                  height: '28px',
                  backgroundColor: isTravelerIssue ? 'error.light' : 'neutral.main',
                }}
              />
              <TicketStatusChip status={ticket.status} onStatusChange={onStatusChange} onCloseTicket={onCloseTicket} />
            </Stack>

            <Stack spacing={2}>
              <DataRow
                label="Assignee"
                value={ticket.assignees?.map((a) => a.name).join(', ') || '—'}
              />
              <DataRow
                label="Traveler"
                action={(
                  <Tooltip title="Travel History">
                    <IconButton onClick={onOpenTravelHistory} sx={{ p: 1 }}>
                      <OutlinedMenuAlt3Icon sx={{ width: 20, height: 20, color: 'text.primary' }} />
                    </IconButton>
                  </Tooltip>
              )}
                value={
                ticket.travelerId ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Link
                      component={RouterLink}
                      to={`/travelers/${ticket.travelerId}`}
                      underline="hover"
                      color="text.secondary"
                      variant="body"
                    >
                      {ticket.travelerName}
                    </Link>
                    {traveler && <TravelerTagsList traveler={traveler} />}
                  </Stack>
                ) : '—'
              }
              />
              <DataRow
                label="Trip Request"
                value={
                ticket.tripRequestId && tripRequestLabel ? (
                  <Link
                    component={RouterLink}
                    to={`/trip-requests/${ticket.tripRequestId}`}
                    underline="hover"
                    color="text.secondary"
                    variant="body"
                  >
                    {tripRequestLabel}
                  </Link>
                ) : '—'
              }
              />

              {tripMatch && (
              <>
                <Divider sx={{ pt: 0.5 }} />
                <DataRow
                  label="Partner"
                  value={
                    tripMatch.partnerId ? (
                      <Link
                        component={RouterLink}
                        to={`/partners/${tripMatch.partnerId}`}
                        underline="hover"
                        color="text.secondary"
                        variant="body"
                      >
                        {tripMatch.partnerName}
                      </Link>
                    ) : '—'
                  }
                  action={
                    partnerCrmProfile ? (
                      <Button
                        size="small"
                        component="a"
                        href={partnerCrmProfile}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontSize: '13px',
                          alignSelf: 'center',
                          px: 1.5,
                          py: 0.5,
                        }}
                      >
                        HubSpot
                      </Button>
                    ) : undefined
                  }
                />
                <DataRow
                  label="Trip Match"
                  value={
                    tripMatchLabel ? (
                      <Link
                        component={RouterLink}
                        to={`/trip-matches/${tripMatch.id}`}
                        underline="hover"
                        color="text.secondary"
                        variant="body"
                      >
                        {tripMatchLabel}
                      </Link>
                    ) : '—'
                  }
                />
                <DataRow
                  label="Total Trip Price (USD)"
                  value={
                    SOLD_STATUSES.has(tripMatch.status) && tripMatch.totalTripPrice
                      ? formatCurrency(tripMatch.totalTripPrice.amountUSD, 'USD')
                      : '—'
                  }
                />
                <DataRow
                  label="Total Zicasso Commission (USD)"
                  value={
                    SOLD_STATUSES.has(tripMatch.status) && totalCommissionUSD != null
                      ? formatCurrency(totalCommissionUSD, 'USD')
                      : '—'
                  }
                />
              </>
              )}

              <Divider sx={{ pt: 0.5 }} />

              <Box>
                <Typography variant="caption" color="text.primary" fontWeight={600} mb={0.5} display="block">
                  Description
                </Typography>
                <Typography
                  variant="body"
                  component="div"
                  color="text.secondary"
                  dangerouslySetInnerHTML={{ __html: ticket.description || '' }}
                  sx={{
                    '& p': { mt: 0, mb: 1.5 },
                    '& p:last-child': { mb: 0 },
                  }}
                />
              </Box>
            </Stack>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          md={7}
          sx={{
            overflowY: 'auto',
            height: '100%',
          }}
        >
          <TicketCommentsPanel
            comments={comments}
            isLoading={isCommentsLoading}
            hasMore={hasMoreComments}
            currentUserId={currentUserId}
            onLoadMore={onLoadMoreComments}
            onCreateComment={onCreateComment}
            onCreateFileComment={onCreateFileComment}
            onUpdateComment={onUpdateComment}
            onDownloadFile={onDownloadFile}
          />
        </Grid>

        <TicketEditModal
          open={isEditModalOpen}
          onClose={onCloseEdit}
          onSave={onSaveTicket}
          assignees={assignees}
          ticket={editableTicket}
          isNew={false}
        />

        <TicketReassignModal
          open={isReassignModalOpen}
          onClose={onCloseReassignModal}
          onSave={onSaveReassign}
          assignees={assignees}
          currentAssigneeIds={reassigningTicketAssigneeIds}
        />

        <TicketDeleteModal
          open={isDeleteModalOpen}
          onClose={onCloseDeleteModal}
          onConfirm={onConfirmDelete}
        />

        {traveler && (
        <TravelHistoryModal
          open={isTravelHistoryOpen}
          onClose={onCloseTravelHistory}
          traveler={traveler}
          selectedTab={travelHistoryTab}
          onTabChange={onTravelHistoryTabChange}
        />
        )}
      </Grid>
    </Container>
  );
};

export default TicketDetailView;
